import React from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { fmtRON } from "../lib/api";

export default function CartDrawer() {
  const { drawerOpen, setDrawerOpen, items, subtotal, shipping, total, setQty, remove } = useCart();

  if (!drawerOpen) return null;

  const amountToFreeShip = Math.max(0, 150 - subtotal);

  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer-overlay">
      <div className="absolute inset-0" style={{ background: "rgba(11,19,43,0.55)", backdropFilter: "blur(4px)" }} onClick={() => setDrawerOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[440px] flex flex-col shadow-2xl" style={{ background: "var(--vdz-cream)" }} data-testid="cart-drawer">
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: "var(--vdz-cream-border)" }}>
          <div>
            <div className="eyebrow">Coșul tău</div>
            <h3 className="font-serif-display text-2xl mt-1">Vis de Zi</h3>
          </div>
          <button onClick={() => setDrawerOpen(false)} aria-label="Închide coș" data-testid="cart-close-button" className="p-2 hover:text-[color:var(--vdz-gold)]">
            <X size={22} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
            <div className="font-serif-display text-xl mb-2">Coșul este gol</div>
            <p className="text-sm opacity-70 mb-6">Descoperă cărțile Vis de Zi.</p>
            <Link to="/carti" onClick={() => setDrawerOpen(false)} className="btn-gold" data-testid="cart-empty-shop-link">Vezi cărțile</Link>
          </div>
        ) : (
          <>
            <div className="px-6 py-3 text-xs" style={{ background: "var(--vdz-cream-warm)" }}>
              {amountToFreeShip > 0 ? (
                <span>Mai adaugă <strong className="gold-text font-serif-display text-base">{fmtRON(amountToFreeShip)}</strong> pentru livrare gratuită ✨</span>
              ) : (
                <span className="gold-text font-serif-accent tracking-[0.18em] uppercase">Felicitări — ai livrare gratuită!</span>
              )}
              <div className="mt-2 h-1 bg-white rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${Math.min(100, (subtotal / 150) * 100)}%`, background: "var(--vdz-gold)" }} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="cart-items-list">
              {items.map((item) => (
                <div key={item.product_id} className="flex gap-4" data-testid={`cart-item-${item.slug}`}>
                  <div className="w-20 h-28 overflow-hidden gold-frame shrink-0" style={{ background: "var(--vdz-navy)" }}>
                    <img src={item.cover_image} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <div className="font-serif-display text-base leading-tight">{item.title}</div>
                    <div className="text-sm mt-1 gold-text font-serif-display">{fmtRON(item.price)}</div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border" style={{ borderColor: "var(--vdz-cream-border)" }}>
                        <button onClick={() => setQty(item.product_id, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center hover:bg-white" aria-label="Scade" data-testid={`cart-qty-decrease-${item.slug}`}>
                          <Minus size={14} />
                        </button>
                        <span className="w-10 text-center text-sm" data-testid={`cart-qty-value-${item.slug}`}>{item.quantity}</span>
                        <button onClick={() => setQty(item.product_id, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-white" aria-label="Crește" data-testid={`cart-qty-increase-${item.slug}`}>
                          <Plus size={14} />
                        </button>
                      </div>
                      <button onClick={() => remove(item.product_id)} aria-label="Șterge" data-testid={`cart-remove-${item.slug}`} className="text-xs opacity-60 hover:text-[color:var(--vdz-burgundy)] flex items-center gap-1">
                        <Trash2 size={14} /> șterge
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t p-6 space-y-2" style={{ borderColor: "var(--vdz-cream-border)" }}>
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span className="font-serif-display text-base" data-testid="cart-subtotal">{fmtRON(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Livrare</span>
                <span className="font-serif-display text-base" data-testid="cart-shipping">{shipping === 0 ? "Gratuit" : fmtRON(shipping)}</span>
              </div>
              <div className="flex justify-between pt-3 mt-2 border-t" style={{ borderColor: "var(--vdz-cream-border)" }}>
                <span className="font-serif-accent uppercase tracking-[0.18em] text-xs">Total</span>
                <span className="font-serif-display text-2xl gold-text" data-testid="cart-total">{fmtRON(total)}</span>
              </div>
              <Link to="/checkout" onClick={() => setDrawerOpen(false)} className="btn-gold w-full mt-3" data-testid="cart-checkout-link">
                Finalizează comanda
              </Link>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
